import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddZoneSidebar from "./AddZoneSidebar";
import ZoneHierarchicalView from "./ZoneHierarchicalView";
import ZoneListingView from "./ZoneListingView";
import ConfirmModal from "../../components/modals/ConfirmModal";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Zones",
    SEARCH_PLACEHOLDER: "Search zones…",
    BUTTON_ADD: "Add Zone",
    BUTTON_EDIT: "Edit",
    BUTTON_DELETE: (count) => `Delete ${count} Zone${count !== 1 ? "s" : ""}`,
    BUTTON_DELETE_LOADING: (done, total) => `Deleting ${done}/${total}`,
    MODAL_TITLE_SINGLE: (name) =>
        name ? `Delete Zone ${name}?` : "Delete Zone?",
    MODAL_TITLE_BULK: (count) =>
        `Delete ${count} Zone${count !== 1 ? "s" : ""}?`,
    VIEW_MODE_LIST: "List View",
    VIEW_MODE_DEFAULT: "Default View",
};

// Confirmation messages
const ZONE_MESSAGES = {
    DELETE_ZONE: (zoneName) => `You are about to delete zone "${zoneName}".

This action cannot be undone. All child zones and installed devices associated with this zone will also be deleted.

Are you sure you wish to continue?`,

    DELETE_ZONES_BULK: (count) => `You are about to delete ${count} zone(s).

This action cannot be undone. All child zones and installed devices associated with these zones will also be deleted.

Are you sure you wish to continue?`,
};

function Zones() {
    const starekContext = useContext(StarekContext);
    const navigator = useNavigate();

    const GET_ZONE_LIST_FOR_PLANT = gql`
        query GetZones($plantId: Int!) {
            zones(where: { plant_id: { equals: $plantId } }) {
                id
                parent_id
                name
                description
                created_at
                updated_at
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
                _count {
                    installed_devices
                }
            }
        }
    `;

    const GET_SHADOW_DEVICES = gql`
        query GetShadowDevices {
            shadowDevices {
                id
                name
            }
        }
    `;

    const DELETE_MANY_ZONES = gql`
        mutation DeleteManyZone($ids: [Int!]!) {
            deleteManyZone(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const DELETE_ONE_ZONE = gql`
        mutation DeleteOneZone($id: Int!) {
            deleteOneZone(where: { id: $id }) {
                id
            }
        }
    `;

    const CREATE_ONE_ZONE = gql`
        mutation CreateOneZone($data: ZoneCreateInput!) {
            createOneZone(data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const UPDATE_ONE_ZONE = gql`
        mutation UpdateOneZone(
            $where: ZoneWhereUniqueInput!
            $data: ZoneUpdateInput!
        ) {
            updateOneZone(where: $where, data: $data) {
                id
                name
                description
                parent {
                    id
                    name
                }
                installed_devices {
                    id
                    name
                }
            }
        }
    `;

    const { data, loading, error, refetch, client } = useQuery(
        GET_ZONE_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { plantId: starekContext.lastUsedPlant.id },
        }
    );

    const [createOneZone] = useMutation(CREATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });
    const [updateOneZone] = useMutation(UPDATE_ONE_ZONE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [showAddZoneDrawer, setShowAddZoneDrawer] = useState(false);
    const [viewMode, setViewMode] = useState("tree");
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [deleteManyZone] = useMutation(DELETE_MANY_ZONES);
    const [deleteOneZone] = useMutation(DELETE_ONE_ZONE);
    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error]);

    useEffect(() => {
        if (!data) return;
        setItems(data.zones);
    }, [data]);

    // Helper function to get all descendant IDs of a zone
    const getDescendantIds = (zoneId, zones) => {
        const descendants = new Set();
        const findChildren = (parentId) => {
            zones.forEach((z) => {
                if (z.parent_id === parentId && !descendants.has(z.id)) {
                    descendants.add(z.id);
                    findChildren(z.id);
                }
            });
        };
        findChildren(zoneId);
        return descendants;
    };

    const parentOptions = useMemo(() => {
        return items.map((z) => ({ value: z.id, label: z.name }));
    }, [items]);

    const getFilteredParentOptions = (currentZone) => {
        if (!currentZone?.id) return parentOptions;

        const excludeIds = getDescendantIds(currentZone.id, items);
        excludeIds.add(currentZone.id);

        return parentOptions.filter((opt) => !excludeIds.has(opt.value));
    };

    const {
        data: shadowData,
        loading: shadowLoading,
        error: shadowError,
    } = useQuery(GET_SHADOW_DEVICES, {
        client: starekContext.engineClient,
        fetchPolicy: "cache-first",
    });
    useEffect(() => {
        if (shadowError) starekContext.showError(shadowError);
    }, [shadowError]);

    const deviceOptions = useMemo(() => {
        const list = shadowData?.shadowDevices ?? [];
        return list.map((d) => ({ value: d.id, label: d.name || d.id }));
    }, [shadowData]);

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveZone = async (zonePayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ??
                starekContext?.user?.id ??
                starekContext?.me?.id;

            if (!currentUserId) {
                console.error(
                    "Missing currentUserId for created_by/updated_by connect"
                );
                throw new Error(
                    "No current user in context; cannot set created_by/updated_by."
                );
            }
            const base = {
                name: zonePayload.name,
                description: zonePayload.description || "",
                plant: { connect: { id: Number(zonePayload.plant_id) } },
            };

            if (!zonePayload.id) {
                const data = {
                    ...base,
                    is_active: zonePayload.is_active ?? true,
                    ...(zonePayload.parent_id != null
                        ? {
                              parent: {
                                  connect: {
                                      id: Number(zonePayload.parent_id),
                                  },
                              },
                          }
                        : {}),
                    ...(Array.isArray(zonePayload.selected_device_ids) &&
                    zonePayload.selected_device_ids.length > 0
                        ? (() => {
                              const ids = zonePayload.selected_device_ids
                                  .map((id) => Number(id))
                                  .filter(Number.isFinite);
                              return ids.length > 0
                                  ? {
                                        installed_devices: {
                                            connect: ids.map((id) => ({ id })),
                                        },
                                    }
                                  : {};
                          })()
                        : {}),
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await createOneZone({ variables: { data } });
            } else {
                const data = {
                    name: { set: base.name },
                    description: { set: base.description },
                    plant: base.plant,
                    ...(zonePayload.parent_id === null
                        ? { parent: { disconnect: {} } }
                        : zonePayload.parent_id !== undefined
                        ? {
                              parent: {
                                  connect: {
                                      id: Number(zonePayload.parent_id),
                                  },
                              },
                          }
                        : {}),
                    ...(Array.isArray(zonePayload.selected_device_ids)
                        ? {
                              installed_devices: {
                                  set: zonePayload.selected_device_ids
                                      .map((id) => Number(id))
                                      .filter(Number.isFinite)
                                      .map((id) => ({ id })),
                              },
                          }
                        : {}),
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await updateOneZone({
                    variables: {
                        where: { id: Number(zonePayload.id) },
                        data,
                    },
                });
            }
            await handleDrawerClose(true, false);
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleAdd = (parentZoneId = null) => {
        if (parentZoneId === null) {
            setSelectedRows([]);
        } else {
            const parentZone = items.find((z) => z.id === parentZoneId);
            if (parentZone) {
                setSelectedRows([
                    {
                        parent: { id: parentZoneId, name: parentZone.name },
                        parent_id: parentZoneId,
                    },
                ]);
            }
        }
        setShowAddZoneDrawer(true);
    };

    const handleEdit = (zoneIdOrNull = null) => {
        if (zoneIdOrNull) {
            // Called from hierarchy view with specific zone ID
            const zoneToEdit = items.find((z) => z.id === zoneIdOrNull);
            if (zoneToEdit) {
                setSelectedRows([zoneToEdit]);
                setShowAddZoneDrawer(true);
            }
        } else {
            // Called from operation bar - use selected rows
            if (selectedRows.length === 1) {
                setShowAddZoneDrawer(true);
            }
        }
    };

    const handleBulkDelete = () => {
        if (selectedRows.length === 0 || isDeleting) return;

        const isSingleSelected = selectedRows.length === 1;
        const title = isSingleSelected
            ? UI_STRINGS.MODAL_TITLE_SINGLE(selectedRows[0].name)
            : UI_STRINGS.MODAL_TITLE_BULK(selectedRows.length);

        openConfirm({
            title: title,
            message: ZONE_MESSAGES.DELETE_ZONES_BULK(selectedRows.length),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            count: selectedRows.length,
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                const ids = selectedRows.map((r) => r.id);

                try {
                    await deleteManyZone({ variables: { ids } });
                    setDeleteDone(ids.length);
                } catch {
                    for (const id of ids) {
                        try {
                            await deleteOneZone({ variables: { id } });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError?.(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddZoneDrawer(false);
        setSelectedRows([]);
        setToggleCleared((p) => !p);

        await new Promise((resolve) => setTimeout(resolve, 0));
        await refetch();

        if (itemDeleted) {
            navigator("/zones");
        }
    };

    const handleDeleteZone = (zone) => {
        if (!zone?.id) return;

        openConfirm({
            title: UI_STRINGS.MODAL_TITLE_SINGLE(zone.name),
            message: ZONE_MESSAGES.DELETE_ZONE(zone.name),
            confirmText: "Delete",
            cancelText: "Cancel",
            type: "danger",
            onConfirm: async () => {
                try {
                    await client.mutate({
                        mutation: DELETE_ONE_ZONE,
                        variables: { id: zone.id },
                    });
                    closeConfirm();
                    await handleDrawerClose(false, true);
                } catch (err) {
                    starekContext.showError(err);
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    if (loading || shadowLoading) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "100vh" }}
            >
                <div className="text-xs d-flex justify-content-center">
                    <i className="fa fa-bullseye fa-10x text-success"></i>
                </div>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Zone", plural: "Zones" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={() => handleAdd()}
                onViewChange={(mode) => setViewMode(mode)}
                onDelete={handleBulkDelete}
                onEdit={() => handleEdit()}
            >
                <EntityOperationBar.View
                    mode="tree"
                    icon="fa-sitemap"
                    tooltip={UI_STRINGS.VIEW_MODE_DEFAULT}
                >
                    <div style={{ height: "700px", width: "100%" }}>
                        <ZoneHierarchicalView
                            zones={items}
                            onAddZone={handleAdd}
                            onEditZone={handleEdit}
                            selectedRows={selectedRows}
                            onSelectedRowsChange={({ selectedRows }) =>
                                setSelectedRows(selectedRows)
                            }
                        />
                    </div>
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <ZoneListingView
                        items={items}
                        filterText={filterText}
                        selectedRows={selectedRows}
                        toggleCleared={toggleCleared}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        UI_STRINGS={UI_STRINGS}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddZoneDrawer && (
                <AddZoneSidebar
                    show={true}
                    item={selectedRows[0] || null}
                    plantId={starekContext.lastUsedPlant.id}
                    parentOptions={getFilteredParentOptions(
                        selectedRows[0] || null
                    )}
                    deviceOptions={deviceOptions}
                    onSave={handleSaveZone}
                    onDelete={handleDeleteZone}
                    onDiscard={() => handleDrawerClose(false, false)}
                />
            )}

            {confirmCfg.open && (
                <ConfirmModal
                    open={confirmCfg.open}
                    type={confirmCfg.type}
                    title={confirmCfg.title}
                    message={confirmCfg.message}
                    confirmText={confirmCfg.confirmText}
                    cancelText={confirmCfg.cancelText}
                    count={confirmCfg.count}
                    onConfirm={confirmCfg.onConfirm}
                    onClose={confirmCfg.onClose}
                />
            )}
        </div>
    );
}

export { Zones };
